import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { AuthorizationService } from '../authorization/authorization.service';
import { ApplicationErrorLogRequest } from '../../shared/models/applicationErrorLogRequest';
import { ApplicationErrorLogResponse } from '../../shared/models/applicationErrorLogResponse';

@Injectable({ providedIn: 'root' })
export class ErrorLogService {
  constructor(private auth: AuthorizationService) {}

  /**
   * Log one or multiple errors to the backend in a single call.
   * Sends JSON array payload to POST /errorlog/saveErrors via AuthorizationService.
   * Fire-and-forget safe with automatic timeout and silent error recovery.
   */
  saveErrors(errors: ApplicationErrorLogRequest | ApplicationErrorLogRequest[]): Observable<ApplicationErrorLogResponse | null> {
    const errorList = Array.isArray(errors) ? errors : [errors];
    if (!errorList.length || !errorList[0]) {
      return of(null);
    }

    const sanitizedList = errorList.map((e) => this.sanitizeRequest(e));

    return this.auth
      .saveErrors(sanitizedList)
      .pipe(
        timeout(4000),
        tap((res) => {
          if (res && (res.statusCode === '0001' || res.statusMessage === 'FAILURE')) {
            console.warn('[ErrorLogService] Logging returned failure status:', res);
          }
        }),
        catchError((err) => {
          console.warn('[ErrorLogService] Failed to send error logs:', err);
          return of(null);
        })
      );
  }

  /**
   * Log a single error (convenience wrapper delegating to saveErrors).
   */
  saveError(error: ApplicationErrorLogRequest): Observable<ApplicationErrorLogResponse | null> {
    return this.saveErrors(error);
  }

  /**
   * Fire-and-forget helper for one or multiple errors.
   */
  logError(error: ApplicationErrorLogRequest): void {
    this.saveErrors(error).subscribe();
  }

  /**
   * Fire-and-forget helper for multiple errors.
   */
  logErrors(errors: ApplicationErrorLogRequest[]): void {
    this.saveErrors(errors).subscribe();
  }

  /**
   * Helper to log UI validation failures.
   */
  logValidationError(pageName: string, fieldName: string, errorMessage: string, fieldValue?: string, userId?: string): void {
    this.logError({
      userId: userId || this.resolveUserIdForRequest(fieldName, fieldValue),
      pageName,
      fieldName,
      fieldValue,
      errorMessage,
      errorType: 'VALIDATION'
    });
  }

  /**
   * Helper to inspect a FormGroup and log detailed validation errors for all invalid controls.
   * Also returns an array of the logged requests.
   */
  logFormValidationErrors(
    pageName: string,
    form: { controls: { [key: string]: any } },
    overallErrorMessage?: string,
    fallbackUserId?: string
  ): ApplicationErrorLogRequest[] {
    const errorRequests: ApplicationErrorLogRequest[] = [];
    const requiredInvalidFields: string[] = [];
    if (!form || !form.controls) {
      return errorRequests;
    }

    Object.keys(form.controls).forEach((controlName) => {
      const control = form.controls[controlName];
      if (control && control.invalid) {
        const errorDetails = this.extractControlErrorDescription(controlName, control.errors);
        const detailedMessage = overallErrorMessage
          ? `${overallErrorMessage} (${errorDetails})`
          : errorDetails;

        const req: ApplicationErrorLogRequest = {
          userId: fallbackUserId || this.resolveUserIdForRequest(controlName, control.value),
          pageName,
          fieldName: controlName,
          fieldValue: control.value !== null && control.value !== undefined ? String(control.value) : undefined,
          errorMessage: detailedMessage,
          errorType: 'VALIDATION'
        };
        errorRequests.push(req);

        if (control.errors && control.errors['required']) {
          requiredInvalidFields.push(controlName);
        }
      }
    });

    if (requiredInvalidFields.length > 1 && overallErrorMessage) {
      errorRequests.push({
        userId: fallbackUserId || this.resolveUserIdForRequest(requiredInvalidFields.join(','), fallbackUserId),
        pageName,
        fieldName: requiredInvalidFields.join(','),
        fieldValue: fallbackUserId,
        errorMessage: `${overallErrorMessage} (Field is required)`,
        errorType: 'VALIDATION'
      });
    }

    if (errorRequests.length > 0) {
      this.logErrors(errorRequests);
    } else if (overallErrorMessage) {
      this.logValidationError(pageName, 'form', overallErrorMessage, fallbackUserId, fallbackUserId);
    }

    return errorRequests;
  }

  /**
   * Translates validation error objects (required, minlength, pattern, etc.) into readable descriptions.
   */
  private extractControlErrorDescription(controlName: string, errors: any): string {
    if (!errors) {
      return `${controlName} is invalid`;
    }
    const reasons: string[] = [];
    if (errors['required']) reasons.push('Field is required');
    if (errors['minlength']) reasons.push(`Minimum length is ${errors['minlength'].requiredLength} (actual ${errors['minlength'].actualLength})`);
    if (errors['maxlength']) reasons.push(`Maximum length is ${errors['maxlength'].requiredLength} (actual ${errors['maxlength'].actualLength})`);
    if (errors['pattern']) reasons.push('Invalid format/pattern mismatch');
    if (errors['email']) reasons.push('Invalid email format');
    if (errors['passwordStrength']) reasons.push('Password does not meet complexity requirements');
    if (errors['mailStrength']) reasons.push('Emails do not match');

    // Handle any custom error keys
    Object.keys(errors).forEach((key) => {
      if (!['required', 'minlength', 'maxlength', 'pattern', 'email', 'passwordStrength', 'mailStrength'].includes(key)) {
        reasons.push(`${key}: ${JSON.stringify(errors[key])}`);
      }
    });

    return reasons.join(', ') || `${controlName} is invalid`;
  }

  /**
   * Helper to log API / backend service errors.
   */
  logApiError(pageName: string, errorMessage: string, fieldName?: string, fieldValue?: string, userId?: string): void {
    this.logError({
      userId: userId || this.resolveUserIdForRequest(fieldName, fieldValue),
      pageName,
      fieldName,
      fieldValue,
      errorMessage,
      errorType: 'API_ERROR'
    });
  }

  /**
   * Sanitizes request payload according to security rules:
   * - Never send fieldValue when fieldName is Password, ConfirmPassword, OTP, etc.
   * - Populates userId from session if omitted.
   */
  private sanitizeRequest(req: ApplicationErrorLogRequest): ApplicationErrorLogRequest {
    const sanitized: ApplicationErrorLogRequest = { ...req };

    if (!sanitized.userId) {
      sanitized.userId =
        sessionStorage.getItem('userId') ||
        sessionStorage.getItem('loginID') ||
        sessionStorage.getItem('username') ||
        undefined;
    }

    if (this.isSensitiveFieldName(sanitized.fieldName)) {
      delete sanitized.fieldValue;
    }

    return sanitized;
  }

  private resolveUserIdForRequest(fieldName?: string, fieldValue?: string): string | undefined {
    const sessionUserId =
      sessionStorage.getItem('userId') ||
      sessionStorage.getItem('loginID') ||
      sessionStorage.getItem('username') ||
      undefined;

    if (sessionUserId) {
      return sessionUserId;
    }

    if (this.isUserIdFieldName(fieldName) && fieldValue) {
      return fieldValue;
    }

    return undefined;
  }

  /**
   * Checks if field name represents passwords, OTPs, or other sensitive credentials.
   */
  private isSensitiveFieldName(fieldName?: string): boolean {
    if (!fieldName) {
      return false;
    }
    const normalized = fieldName.toLowerCase().replace(/[\s_-]/g, '');
    return (
      normalized.includes('password') ||
      normalized.includes('confirmpassword') ||
      normalized.includes('repassword') ||
      normalized.includes('pwd') ||
      normalized.includes('otp') ||
      normalized.includes('mfatoken') ||
      normalized.includes('secret') ||
      normalized.includes('pin')
    );
  }

  private isUserIdFieldName(fieldName?: string): boolean {
    if (!fieldName) {
      return false;
    }

    const normalized = fieldName.toLowerCase().replace(/[\s_-]/g, '');
    return (
      normalized.includes('userid') ||
      normalized.includes('username') ||
      normalized.includes('loginid')
    );
  }
}
